/**
 * The form for the builder's selected tile: title, chart type, then only the
 * field controls that chart type takes (VIS_SHAPE), sort, row limit and width.
 * Messages come from the shared zod (issuesOf), so they match the server's.
 *
 * @module components/looker/admin/builder/BuilderTileForm
 */
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { LookerBuilderField } from '@/services/looker/lookerApi';
import { APP_WIDGETS, type AppWidget } from '@/services/looker/lookerAppTiles';
import { SPEC_VIS, VIS_SHAPE, tileFields, type SpecTile, type SpecVis } from '@/services/looker/lookerTemplateSpec';
import { withField, withVis } from './builderDraft';

interface Props {
  tile: SpecTile;
  fields: readonly LookerBuilderField[];
  /** This tile's messages, by field. */
  issues: Record<string, string>;
  onChange: (tile: SpecTile) => void;
}

const INPUT = 'w-full rounded-md border border-neutral-300 px-2 py-1 text-sm';
const LABEL = 'block text-sm font-medium text-ink-900';

const Issue: React.FC<{ id: string; text?: string }> = ({ id, text }) =>
  text ? <p id={id} className="mt-1 text-xs text-red-700">{text}</p> : null;

export const BuilderTileForm: React.FC<Props> = ({ tile, fields, issues, onChange }) => {
  const { t } = useTranslation();
  const id = useId();
  const shape = VIS_SHAPE[tile.vis];
  const dimensions = fields.filter((f) => f.kind === 'dimension');
  const measures = fields.filter((f) => f.kind === 'measure');
  const labelOf = (name: string) => fields.find((f) => f.name === name)?.label ?? name;

  // Literal keys, so the locale integrity test sees every one of them.
  const visName: Record<SpecVis, string> = {
    single_value: t('looker.builder.visTypes.single_value'),
    line: t('looker.builder.visTypes.line'),
    area: t('looker.builder.visTypes.area'),
    column: t('looker.builder.visTypes.column'),
    bar: t('looker.builder.visTypes.bar'),
    pie: t('looker.builder.visTypes.pie'),
    donut: t('looker.builder.visTypes.donut'),
    funnel: t('looker.builder.visTypes.funnel'),
    waterfall: t('looker.builder.visTypes.waterfall'),
    scatter: t('looker.builder.visTypes.scatter'),
    table: t('looker.builder.visTypes.table'),
    app: t('looker.builder.visTypes.app'),
  };
  const widgetName: Record<AppWidget, string> = {
    attention: t('looker.app.widgets.attention'),
    dataQuality: t('looker.app.widgets.dataQuality'),
    outcomes: t('looker.app.widgets.outcomes'),
    budgetPacing: t('looker.app.widgets.budgetPacing'),
  };
  const isApp = tile.vis === 'app';

  const set = (patch: Partial<SpecTile>) => onChange({ ...tile, ...patch });
  /** An optional field: '' removes it (and a sort on it). */
  const setOptional = (key: 'dimension' | 'pivot', value: string) => {
    onChange(withField(tile, key, value));
  };
  const toggleMeasure = (name: string, on: boolean) => {
    const next = on ? [...tile.measures, name] : tile.measures.filter((m) => m !== name);
    const keptSort = tile.sort && (on || tile.sort.field !== name) ? tile.sort : undefined;
    if (on) {
      onChange({ ...tile, measures: next, sort: keptSort });
      return;
    }
    // A measure that goes also leaves every option that named it.
    const { shareOf, fadeBelow, compareMeasures, lineMeasures, rightAxis, ...rest } = tile;
    const without = (xs: string[] | undefined) => {
      const kept = xs?.filter((m) => m !== name);
      return kept && kept.length > 0 ? kept : undefined;
    };
    onChange({
      ...rest,
      measures: next,
      sort: keptSort,
      ...(shareOf && shareOf !== name ? { shareOf } : {}),
      ...(fadeBelow && fadeBelow.field !== name ? { fadeBelow } : {}),
      ...(without(compareMeasures) ? { compareMeasures: without(compareMeasures) } : {}),
      ...(without(lineMeasures) ? { lineMeasures: without(lineMeasures) } : {}),
      ...(without(rightAxis) ? { rightAxis: without(rightAxis) } : {}),
    });
  };
  /** Adds or removes one measure from a list option; an empty list is removed. */
  const toggleIn = (key: 'compareMeasures' | 'lineMeasures' | 'rightAxis', name: string, on: boolean) => {
    const current = tile[key] ?? [];
    const next = on ? [...current, name] : current.filter((m) => m !== name);
    const { [key]: _old, ...rest } = tile;
    onChange(next.length > 0 ? { ...rest, [key]: next } : rest);
  };
  const isTable = tile.vis === 'table';
  const isCombo = tile.vis === 'line' || tile.vis === 'area' || tile.vis === 'column';
  const isPie = tile.vis === 'pie' || tile.vis === 'donut';
  const canCompare = isTable || tile.vis === 'single_value';
  const chosen = measures.filter((m) => tile.measures.includes(m.name));
  const widthLabel: Record<SpecTile['width'], string> = {
    sixth: t('looker.builder.widths.sixth'),
    third: t('looker.builder.widths.third'),
    half: t('looker.builder.widths.half'),
    full: t('looker.builder.widths.full'),
  };
  const widths: ReadonlyArray<SpecTile['width']> = tile.vis === 'single_value' ? ['sixth', 'third', 'half', 'full'] : ['third', 'half', 'full'];
  // (an app box takes the same widths as any non-single-value tile: no sixth)

  return (
    <div className="space-y-4">
      <div>
        <label className={LABEL} htmlFor={`${id}-title`}>{t('looker.builder.tileTitle')}</label>
        <input id={`${id}-title`} className={INPUT} value={tile.title} maxLength={100}
          aria-describedby={issues.title ? `${id}-title-issue` : undefined}
          onChange={(e) => set({ title: e.target.value })} />
        <Issue id={`${id}-title-issue`} text={issues.title} />
      </div>

      <div>
        <label className={LABEL} htmlFor={`${id}-vis`}>{t('looker.builder.vis')}</label>
        <select id={`${id}-vis`} className={INPUT} value={tile.vis} onChange={(e) => onChange(withVis(tile, e.target.value as SpecVis))}>
          {SPEC_VIS.map((v) => <option key={v} value={v}>{visName[v]}</option>)}
        </select>
      </div>

      {isApp && (
        <div>
          <label className={LABEL} htmlFor={`${id}-widget`}>{t('looker.builder.widget')}</label>
          <select id={`${id}-widget`} className={INPUT} value={tile.widget ?? ''} onChange={(e) => set({ widget: e.target.value as AppWidget })}>
            {APP_WIDGETS.map((w) => <option key={w} value={w}>{widgetName[w]}</option>)}
          </select>
          <Issue id={`${id}-widget-issue`} text={issues.widget} />
        </div>
      )}

      {!isApp && (<>
      <fieldset aria-describedby={issues.measures ? `${id}-measures-issue` : undefined}>
        <legend className={LABEL}>{t('looker.builder.measures')}</legend>
        <div className="mt-1 grid max-h-48 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
          {measures.map((m) => (
            <label key={m.name} className="flex items-center gap-2 text-sm text-ink-900">
              <input type="checkbox" checked={tile.measures.includes(m.name)} onChange={(e) => toggleMeasure(m.name, e.target.checked)} />
              {m.label}
            </label>
          ))}
        </div>
        <Issue id={`${id}-measures-issue`} text={issues.measures} />
      </fieldset>

      {shape.dimension !== 'none' && (
        <div>
          <label className={LABEL} htmlFor={`${id}-dimension`}>{t('looker.builder.dimension')}</label>
          <select id={`${id}-dimension`} className={INPUT} value={tile.dimension ?? ''} onChange={(e) => setOptional('dimension', e.target.value)}>
            <option value="">{t('looker.builder.none')}</option>
            {dimensions.map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
          </select>
          <Issue id={`${id}-dimension-issue`} text={issues.dimension} />
        </div>
      )}

      {shape.pivot && (
        <div>
          <label className={LABEL} htmlFor={`${id}-pivot`}>{t('looker.builder.pivot')}</label>
          <select id={`${id}-pivot`} className={INPUT} value={tile.pivot ?? ''} onChange={(e) => setOptional('pivot', e.target.value)}>
            <option value="">{t('looker.builder.none')}</option>
            {dimensions.map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
          </select>
          <Issue id={`${id}-pivot-issue`} text={issues.pivot} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={LABEL} htmlFor={`${id}-sort`}>{t('looker.builder.sort')}</label>
          <select id={`${id}-sort`} className={INPUT} value={tile.sort?.field ?? ''}
            onChange={(e) => set({ sort: e.target.value ? { field: e.target.value, desc: tile.sort?.desc ?? true } : undefined })}>
            <option value="">{t('looker.builder.sortDefault')}</option>
            {tileFields(tile).map((f) => <option key={f} value={f}>{labelOf(f)}</option>)}
          </select>
          {tile.sort && (
            <label className="mt-1 flex items-center gap-2 text-sm text-ink-900">
              <input type="checkbox" checked={tile.sort.desc}
                onChange={(e) => set({ sort: { field: tile.sort?.field ?? '', desc: e.target.checked } })} />
              {t('looker.builder.sortDesc')}
            </label>
          )}
          <Issue id={`${id}-sort-issue`} text={issues.sort} />
        </div>
        <div>
          <label className={LABEL} htmlFor={`${id}-limit`}>{t('looker.builder.limit')}</label>
          <input id={`${id}-limit`} className={INPUT} type="number" min={1} max={5000} inputMode="numeric"
            value={tile.limit ?? ''} placeholder={t('looker.builder.limitDefault')}
            onChange={(e) => set({ limit: e.target.value === '' ? undefined : Number(e.target.value) })} />
          <Issue id={`${id}-limit-issue`} text={issues.limit} />
        </div>
      </div>

      <fieldset className="space-y-3">
        <legend className={LABEL}>{t('looker.builder.options')}</legend>
        <label className="flex items-center gap-2 text-sm text-ink-900">
          <input type="checkbox" checked={tile.showWhen === 'oneObjective'}
            onChange={(e) => {
              const { showWhen: _s, ...rest } = tile;
              onChange(e.target.checked ? { ...rest, showWhen: 'oneObjective' } : rest);
            }} />
          {t('looker.builder.showWhenOneObjective')}
        </label>
        {canCompare && (
          <label className="flex items-center gap-2 text-sm text-ink-900">
            <input type="checkbox" checked={tile.compare === true}
              onChange={(e) => {
                const { compare: _c, compareMeasures: _m, ...rest } = tile;
                onChange(e.target.checked ? { ...rest, compare: true, ...(tile.compareMeasures ? { compareMeasures: tile.compareMeasures } : {}) } : rest);
              }} />
            {t('looker.builder.compare')}
          </label>
        )}
        <Issue id={`${id}-compare-issue`} text={issues.compare} />
        {isTable && tile.compare && (
          <fieldset>
            <legend className="text-sm text-ink-900">{t('looker.builder.compareMeasures')}</legend>
            <div className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
              {chosen.map((m) => (
                <label key={m.name} className="flex items-center gap-2 text-sm text-ink-900">
                  <input type="checkbox" checked={tile.compareMeasures?.includes(m.name) ?? false}
                    onChange={(e) => toggleIn('compareMeasures', m.name, e.target.checked)} />
                  {m.label}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <Issue id={`${id}-compareMeasures-issue`} text={issues.compareMeasures} />
        {isTable && (
          <>
            <div>
              <label className={LABEL} htmlFor={`${id}-share`}>{t('looker.builder.shareOf')}</label>
              <select id={`${id}-share`} className={INPUT} value={tile.shareOf ?? ''}
                onChange={(e) => {
                  const { shareOf: _s, ...rest } = tile;
                  onChange(e.target.value ? { ...rest, shareOf: e.target.value } : rest);
                }}>
                <option value="">{t('looker.builder.none')}</option>
                {chosen.map((m) => <option key={m.name} value={m.name}>{m.label}</option>)}
              </select>
              <Issue id={`${id}-share-issue`} text={issues.shareOf} />
            </div>
            <div>
              <label className={LABEL} htmlFor={`${id}-fade`}>{t('looker.builder.fadeField')}</label>
              <div className="flex items-center gap-2">
                <select id={`${id}-fade`} className={INPUT} value={tile.fadeBelow?.field ?? ''}
                  onChange={(e) => {
                    const { fadeBelow: _f, ...rest } = tile;
                    onChange(e.target.value ? { ...rest, fadeBelow: { field: e.target.value, value: tile.fadeBelow?.value ?? 0 } } : rest);
                  }}>
                  <option value="">{t('looker.builder.none')}</option>
                  {chosen.map((m) => <option key={m.name} value={m.name}>{m.label}</option>)}
                </select>
                {tile.fadeBelow && (
                  <>
                    <label className="whitespace-nowrap text-sm text-ink-900" htmlFor={`${id}-fade-value`}>{t('looker.builder.fadeValue')}</label>
                    <input id={`${id}-fade-value`} className={INPUT} type="number" min={0} inputMode="decimal" value={tile.fadeBelow.value}
                      onChange={(e) => set({ fadeBelow: { field: tile.fadeBelow?.field ?? '', value: Number(e.target.value) } })} />
                  </>
                )}
              </div>
              <Issue id={`${id}-fade-issue`} text={issues.fadeBelow} />
            </div>
            <div>
              <label className={LABEL} htmlFor={`${id}-extra`}>{t('looker.builder.extraDimension')}</label>
              <select id={`${id}-extra`} className={INPUT} value={tile.extraDimensions?.[0] ?? ''}
                onChange={(e) => {
                  const { extraDimensions: _e, ...rest } = tile;
                  onChange(e.target.value ? { ...rest, extraDimensions: [e.target.value] } : rest);
                }}>
                <option value="">{t('looker.builder.none')}</option>
                {dimensions.filter((d) => d.name !== tile.dimension && d.name !== tile.pivot).map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
              </select>
              <Issue id={`${id}-extra-issue`} text={issues.extraDimensions} />
            </div>
          </>
        )}
        {isCombo && (
          <>
            {chosen.map((m) => (
              <div key={m.name} className="flex flex-wrap items-center gap-4 text-sm text-ink-900">
                <span className="min-w-24 font-medium">{m.label}</span>
                <label className="flex items-center gap-2">
                  <input type="checkbox" aria-label={`${m.label}: ${t('looker.builder.asLine')}`} checked={tile.lineMeasures?.includes(m.name) ?? false}
                    onChange={(e) => toggleIn('lineMeasures', m.name, e.target.checked)} />
                  {t('looker.builder.asLine')}
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" aria-label={`${m.label}: ${t('looker.builder.rightAxis')}`} checked={tile.rightAxis?.includes(m.name) ?? false}
                    onChange={(e) => toggleIn('rightAxis', m.name, e.target.checked)} />
                  {t('looker.builder.rightAxis')}
                </label>
              </div>
            ))}
            <Issue id={`${id}-lineMeasures-issue`} text={issues.lineMeasures ?? issues.rightAxis} />
            {tile.dimension === 'date_date' && (
              <label className="flex items-center gap-2 text-sm text-ink-900">
                <input type="checkbox" checked={tile.autoGrain === true}
                  onChange={(e) => {
                    const { autoGrain: _a, ...rest } = tile;
                    onChange(e.target.checked ? { ...rest, autoGrain: true } : rest);
                  }} />
                {t('looker.builder.autoGrain')}
              </label>
            )}
            <Issue id={`${id}-autoGrain-issue`} text={issues.autoGrain} />
          </>
        )}
        {isPie && (
          <label className="flex items-center gap-2 text-sm text-ink-900">
            <input type="checkbox" checked={tile.listAbsent === true}
              onChange={(e) => {
                const { listAbsent: _l, ...rest } = tile;
                onChange(e.target.checked ? { ...rest, listAbsent: true } : rest);
              }} />
            {t('looker.builder.listAbsent')}
          </label>
        )}
        <Issue id={`${id}-listAbsent-issue`} text={issues.listAbsent} />
      </fieldset>
      </>)}

      <fieldset>
        <legend className={LABEL}>{t('looker.builder.width')}</legend>
        <div className="mt-1 flex gap-4">
          {widths.map((w) => (
            <label key={w} className="flex items-center gap-2 text-sm text-ink-900">
              <input type="radio" name={`${id}-width`} checked={tile.width === w} onChange={() => set({ width: w })} />
              {widthLabel[w]}
            </label>
          ))}
        </div>
      </fieldset>
      <Issue id={`${id}-width-issue`} text={issues.width} />
      <Issue id={`${id}-key-issue`} text={issues.key ?? issues.tile} />
    </div>
  );
};
